import type { MeetingBot } from "@/lib/queries/meetings";

export const STATUS_OPTIONS = [
  "All",
  "SCHEDULED",
  "PREPARATION",
  "ACTIVE",
  "ENDED",
  "ERROR",
] as const;

export const SEVERITY_OPTIONS = ["All", "Critical", "Warning", "Safe"] as const;

export const PLATFORM_OPTIONS = ["All", "google", "zoom", "teams"] as const;

export type StatusFilter = (typeof STATUS_OPTIONS)[number];
export type SeverityFilter = (typeof SEVERITY_OPTIONS)[number];
export type PlatformFilter = (typeof PLATFORM_OPTIONS)[number];

export type MeetingThreat = {
  label: string;
  tone: "critical" | "warning";
  kind: "ai" | "pii" | "voice";
};

export type MeetingSeverity = "critical" | "warning" | "safe";

const AVATAR_COLORS = [
  "bg-pink-100 text-pink-700 dark:bg-[#22262F] dark:text-[#CECFD2]",
  "bg-blue-100 text-blue-700 dark:bg-[#22262F] dark:text-[#CECFD2]",
  "bg-green-100 text-green-700 dark:bg-[#22262F] dark:text-[#CECFD2]",
  "bg-orange-100 text-orange-700 dark:bg-[#22262F] dark:text-[#CECFD2]",
  "bg-purple-100 text-purple-700 dark:bg-[#22262F] dark:text-[#CECFD2]",
];

export function formatStatusLabel(status: string): string {
  if (status === "All") return "All";
  if (status === "ACTIVE") return "Live";
  if (status === "ERROR") return "Unsuccessful";
  if (status === "POST_PROCESSING") return "Post-Processing";
  return status.charAt(0) + status.slice(1).toLowerCase();
}

export function formatPlatform(platform: string): string {
  switch (platform.toLowerCase()) {
    case "google":
      return "Google Meet";
    case "teams":
      return "Teams";
    case "zoom":
      return "Zoom";
    default:
      return platform.charAt(0).toUpperCase() + platform.slice(1);
  }
}

export function formatPlatformFilterLabel(platform: PlatformFilter): string {
  if (platform === "All") return "All";
  switch (platform) {
    case "google":
      return "Google Meet";
    case "zoom":
      return "Zoom";
    case "teams":
      return "Microsoft Teams";
  }
}

export function matchesPlatformFilters(
  meeting: MeetingBot,
  platformFilters: Array<PlatformFilter>,
): boolean {
  if (platformFilters.length === 0 || platformFilters.includes("All"))
    return true;
  return platformFilters.includes(meeting.platform as PlatformFilter);
}

export function formatMeetingDuration(meeting: MeetingBot): string | null {
  const start = meeting.meeting_start_time
    ? new Date(meeting.meeting_start_time).getTime()
    : null;
  const end = meeting.left_at ? new Date(meeting.left_at).getTime() : null;

  if (!start || !end || end <= start) return null;

  const minutes = Math.round((end - start) / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

export function getAiPercentage(meeting: MeetingBot): number | null {
  const analytics = meeting.analytics;
  if (!analytics) return null;

  const { ai, human } = analytics.ai_content_detection;
  if (ai == null || human == null || (ai === 0 && human === 0)) return null;
  return Math.round((ai / (ai + human)) * 100);
}

export function getFakeVoicePercentage(meeting: MeetingBot): number | null {
  const fake = meeting.analytics?.voice_authentication.fake_ratio;
  if (fake == null) return null;
  return Math.round(fake);
}

export function getMeetingThreats(meeting: MeetingBot): Array<MeetingThreat> {
  const threats: Array<MeetingThreat> = [];
  const aiPercentage = getAiPercentage(meeting);
  const piiCount = meeting.analytics?.pii.count ?? 0;
  const fakeVoice = getFakeVoicePercentage(meeting);

  if (aiPercentage != null && aiPercentage >= 20) {
    threats.push({
      label: `${aiPercentage}% AI`,
      tone: aiPercentage >= 50 ? "critical" : "warning",
      kind: "ai",
    });
  }

  if (piiCount > 0) {
    threats.push({
      label: `${piiCount} PII`,
      tone: piiCount >= 10 ? "critical" : "warning",
      kind: "pii",
    });
  }

  if (fakeVoice != null && fakeVoice >= 20) {
    threats.push({
      label: `${fakeVoice}% Voice`,
      tone: fakeVoice >= 50 ? "critical" : "warning",
      kind: "voice",
    });
  }

  return threats.slice(0, 3);
}

export function getMeetingSeverity(meeting: MeetingBot): MeetingSeverity {
  const riskScore = meeting.analytics?.risk_score.total;

  if (riskScore == null || riskScore < 50) return "safe";
  if (riskScore <= 70) return "warning";
  return "critical";
}

export function getSeverityAccentClass(severity: MeetingSeverity): string {
  switch (severity) {
    case "critical":
      return "bg-[#D92D20]";
    case "warning":
      return "bg-[#F79009]";
    case "safe":
      return "bg-[#099250]";
  }
}

export function getSeverityLabel(severity: MeetingSeverity): string {
  switch (severity) {
    case "critical":
      return "Critical";
    case "warning":
      return "Warning";
    case "safe":
      return "Safe";
  }
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function getAvatarColor(name: string): string {
  const index =
    name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

export function formatMeetingListDate(dateString: string | null): string {
  if (!dateString) return "--";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
